/**
 * Public storefront bundle tile (TNK-212 phase 2a). Sized to drop straight
 * into the singles grid (CardGrid) as a same-shape cell, so bundles blend in
 * with the cards. Single cover image (operator's custom photo, else a member
 * card's photo, else a placeholder) + a "Bundle" badge and card count. Links
 * to the detail page; the buy/reserve path lands in phase 2b.
 */
import { Link } from "@tanstack/react-router";
import { Package, ShoppingCart } from "lucide-react";

import { imageUrl } from "@/features/imageUpload";
import type { StorefrontBundle } from "@/features/storefront";
import { useShopCart } from "@/routes/shop";

export function BundleCard({ bundle }: { bundle: StorefrontBundle }) {
  const img = bundle.coverRef ? imageUrl(bundle.coverRef, "thumb") : null;
  const cart = useShopCart();
  const inCart = cart.hasProduct(bundle.id);

  return (
    // Outer h-full grid-item wrapper mirrors CardItem's motion.div so the tile
    // STRETCHES to the row height; the inner flex-1 then drops the price row to
    // the bottom, aligning it with the cards (which carry an extra badges row).
    <div className="h-full">
      <Link
        to="/shop/bundle/$id"
        params={{ id: bundle.id }}
        className="group relative flex h-full flex-col overflow-hidden rounded-lg border border-border bg-card transition-all hover:-translate-y-1"
        style={{ boxShadow: "0 4px 16px rgba(0,0,0,0.4)" }}
        onMouseEnter={(e) => {
          e.currentTarget.style.borderColor = "rgba(201,168,76,0.4)";
          e.currentTarget.style.boxShadow =
            "0 8px 32px rgba(0,0,0,0.6), 0 0 16px rgba(201,168,76,0.45)";
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.borderColor = "";
          e.currentTarget.style.boxShadow = "0 4px 16px rgba(0,0,0,0.4)";
        }}
      >
        {/* Gold accent stripe — the bundle equivalent of CardItem's game stripe */}
        <div
          className="absolute left-0 right-0 top-0 z-10 h-0.5"
          style={{
            background:
              "linear-gradient(90deg, transparent, var(--color-primary), transparent)",
          }}
        />

        <div className="relative p-2 pb-1">
          <div
            className="relative w-full overflow-hidden rounded bg-muted"
            style={{ aspectRatio: "2.5 / 3.5" }}
          >
            {img ? (
              <img
                src={img}
                alt={bundle.name}
                className="h-full w-full object-cover"
                loading="lazy"
              />
            ) : (
              <div className="flex h-full w-full items-center justify-center text-muted-foreground">
                <Package size={36} />
              </div>
            )}
          </div>
        </div>

        {/* Top-packed like CardItem so the tile aligns with the cards: name +
            subtitle, then the "Bundle" chip in the same slot as a card's
            rarity/condition chips, then the price row. */}
        <div className="flex flex-col gap-1.5 px-3 pb-3">
          <div>
            <h3
              className="shop-display truncate leading-tight"
              style={{
                fontSize: "13px",
                fontWeight: 600,
                color: "var(--color-foreground)",
              }}
            >
              {bundle.name}
            </h3>
            <p className="mt-0.5 truncate text-[10px] text-muted-foreground">
              {bundle.cardCount} card{bundle.cardCount === 1 ? "" : "s"}
              {bundle.productType ? ` · ${bundle.productType}` : ""}
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-1.5">
            <span
              className="flex items-center gap-1 rounded-sm px-1.5 py-0.5 text-[9px] font-semibold uppercase tracking-wider"
              style={{
                background: "rgba(201,168,76,0.18)",
                border: "1px solid rgba(201,168,76,0.4)",
                color: "var(--color-primary)",
              }}
            >
              <Package size={10} />
              Bundle
            </span>
          </div>
          <div className="mt-1 flex items-center justify-between">
            <span
              className="shop-mono text-base font-bold"
              style={{ color: "var(--color-primary)" }}
            >
              ${bundle.price.toFixed(2)}
            </span>
            <button
              type="button"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                if (inCart) cart.removeProduct(bundle.id);
                else cart.addProduct(bundle.id);
              }}
              className="flex items-center gap-1 rounded-sm px-2.5 py-1.5 text-[11px] font-semibold transition-colors"
              style={{
                background: inCart
                  ? "rgba(74,222,128,0.15)"
                  : "rgba(201,168,76,0.15)",
                border: inCart
                  ? "1px solid rgba(74,222,128,0.4)"
                  : "1px solid rgba(201,168,76,0.35)",
                color: inCart ? "#4ade80" : "var(--color-primary)",
              }}
            >
              <ShoppingCart size={11} />
              {inCart ? "Added" : "Add"}
            </button>
          </div>
        </div>
      </Link>
    </div>
  );
}
